import React, { useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";

import invariant from "tiny-invariant";

import { useAppSelector } from "../../hooks/reduxHooks";
import useBreakpoint from "../../hooks/useBreakpoint";
import { AUTH_LEVELS, TOURNAMENT_LOGO_URL } from "../../lib/globals";
import { defaultDateFormat, isXOrBigger, isXOrSmaller } from "../../lib/utils";

interface Filters {
    tournament: string | undefined;
    ageLevels: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    designation: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
}

const TournamentsTable = (): React.JSX.Element => {
    const breakpoint = useBreakpoint();
    const { tournaments: childTournaments, masterTournaments: tournaments } =
        useAppSelector(state => state.tournamentmanagement);
    const user = useAppSelector(state => state.auth.user);
    invariant(user);

    const isAdmin = AUTH_LEVELS.sitemanagement.includes(user.userLevel);

    const [filters, setFilters] = useState<Filters>({
        tournament: "",
        ageLevels: {
            options: Array.from(
                new Set(
                    childTournaments
                        .filter(t => t.ageLevel)
                        .map(t => t.ageLevel as string)
                        .sort((a, b) => parseInt(a) - parseInt(b)),
                ),
            ),
            filter: undefined,
        },
        designation: {
            options: Array.from(
                new Set(
                    tournaments
                        .filter(t => t.designation !== null)
                        .map(t => t.designation)
                        .sort(),
                ),
            ) as string[],
            filter: undefined,
        },
    });

    return (
        <>
            <Row className="tournamentFilters">
                <Col sm={6}>
                    <h4>Tournament Name:</h4>
                    <Form.Control
                        type="text"
                        placeholder="Tournament Name"
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                tournament:
                                    e.target.value.trim() === ""
                                        ? undefined
                                        : e.target.value.trim(),
                            }));
                        }}
                    />
                </Col>
                <Col sm={6} lg={4} xl={3}>
                    <h4>Age Level:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.ageLevels.options.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                ageLevels: {
                                    ...prev.ageLevels,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col sm={6} lg={4} xl={3} className="pt-2 pt-sm-0">
                    <h4>Competition Level:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.designation.options.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                designation: {
                                    ...prev.designation,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
            </Row>
            <Table className="tournamentsTable" striped bordered>
                <thead>
                    <tr>
                        <th>Tournament Name</th>
                        <th>Dates</th>
                        <th>Age Levels</th>
                        <th>
                            {isXOrSmaller("sm", breakpoint)
                                ? "Comp."
                                : "Competition"}{" "}
                            Level
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {tournaments
                        .filter(t =>
                            user.userLevel === 3
                                ? t.tournamentDirector
                                      ?.flatMap(t => t.id)
                                      .includes(user.id)
                                : true,
                        )
                        .filter(t =>
                            filters.tournament
                                ? t.name
                                      ?.toLowerCase()
                                      .includes(
                                          filters.tournament.toLowerCase(),
                                      )
                                : true,
                        )
                        .filter(t =>
                            filters.ageLevels.filter
                                ? childTournaments
                                      .filter(c => c.parent === t.id)
                                      .some(child =>
                                          filters.ageLevels.filter?.includes(
                                              child.ageLevel,
                                          ),
                                      )
                                : true,
                        )
                        .filter(t =>
                            filters.designation.filter
                                ? t.designation
                                    ? filters.designation.filter.includes(
                                          t.designation,
                                      )
                                    : false
                                : true,
                        )
                        .map(t => (
                            <tr key={t.id}>
                                <td
                                    width={
                                        isXOrBigger("md", breakpoint)
                                            ? "50%"
                                            : ""
                                    }
                                    className="tournamentInfo"
                                >
                                    <div className="tournamentInfoContainer">
                                        <div className="tournamentLogo">
                                            {t.logo && (
                                                <Link
                                                    to={`./tournament/${t.id}/edit`}
                                                >
                                                    <img
                                                        src={`${TOURNAMENT_LOGO_URL}${t.logo}`}
                                                        alt={`${t.name} logo`}
                                                        className="tournamentLogo"
                                                    />
                                                </Link>
                                            )}
                                        </div>
                                        <div className="tournamentInfo">
                                            <Link
                                                to={`./tournament/${t.id}/edit`}
                                            >{`${t.name}`}</Link>
                                            <br />
                                            {`${t.field?.name}`}
                                        </div>
                                    </div>
                                </td>
                                <td
                                    width={
                                        isXOrSmaller("sm", breakpoint)
                                            ? "15%"
                                            : ""
                                    }
                                    className="tournamentDates"
                                >
                                    {`${t.startDate && defaultDateFormat.format(new Date(t.startDate))} - ${t.endDate && defaultDateFormat.format(new Date(t.endDate))}`}
                                </td>
                                <td className="tournamentAgeLevels">
                                    <ul>
                                        {childTournaments
                                            .filter(ct => ct.parent == t.id)
                                            .map(t => (
                                                <li key={t.id}>
                                                    {isAdmin ? (
                                                        <Link
                                                            to={`./tournament/${t.id}/edit`}
                                                        >
                                                            {t.ageLevel}{" "}
                                                        </Link>
                                                    ) : (
                                                        `${t.ageLevel} `
                                                    )}
                                                    {isXOrSmaller(
                                                        "sm",
                                                        breakpoint,
                                                    ) && <br />}
                                                    (
                                                    {t.teams
                                                        ? t.teams.length
                                                        : 0}{" "}
                                                    /{" "}
                                                    {t.maxTeams
                                                        ? t.maxTeams
                                                        : "-"}
                                                    )
                                                </li>
                                            ))}
                                    </ul>
                                </td>
                                <td>{t.designation ? t.designation : ""}</td>
                            </tr>
                        ))}
                </tbody>
            </Table>
        </>
    );
};

export default TournamentsTable;
